import { FormLabel, Stack } from '@mui/joy';
import { Plus } from '@phosphor-icons/react';
import { KeyboardEvent, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Form, FormField, IManagedNote, Notes } from 'core/components';
import { FormGroupWrapper } from 'core/elements';
import { InputType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { isEnterKeyPress, isTabKeyPress, preventEnter } from 'core/utils';
import { AddNoteButton } from 'partBoms/components/PartBomDetails';
import { usePartBomItemActions } from 'partBoms/hooks';
import { IPartBomItem } from 'partBoms/types';
import { AdvancedPartNumberFormField } from 'parts/elements';
import { PartType } from 'parts/enums';
import { PartNumberHelper } from 'parts/helpers';
import { manufacturerService } from 'parts/services';
import { IManufacturer, IPart, IUom } from 'parts/types';

import { IUpsertItemFormData } from './types';
import { useNotes } from './useNotes';
import { getFocusElementNameAfterValidate } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IUpsertItemFormData>) => void;
  partBomItem?: IPartBomItem;
  onSubmit: (reset: UseFormReset<IUpsertItemFormData>) => (data: IUpsertItemFormData) => void;
}

export const UpsertItemFormFields = ({ isOpen, onClose, partBomItem, onSubmit }: IProps) => {
  const [isValidating, setIsValidating] = useState(false);
  const [uomOptions, setUomOptions] = useState<IUom[]>([]);
  const [verifiedPart, setVerifiedPart] = useState(
    Boolean(partBomItem?.partArr?.verified) || false
  );

  const { t } = useTranslation();

  const { setFocusElementName } = useFocusInputFunctionality();

  const { setValue, getValues, reset, watch } = useFormContext();

  const { user } = useAuthUser();
  const { validatePart } = usePartBomItemActions();

  useEffect(() => {
    if (isOpen) {
      setFocusElementName('part');
    }
  }, [isOpen, setFocusElementName]);

  useEffect(() => {
    if (!partBomItem?.id) {
      setUomOptions([]);
      setVerifiedPart(false);

      return;
    }

    setUomOptions(partBomItem.allowedUoms.map((uom: string) => ({ id: uom, name: uom })));
    setVerifiedPart(Boolean(partBomItem.partArr?.verified));
  }, [partBomItem, setValue, setVerifiedPart]);

  const isEdit = useMemo(() => !!partBomItem?.id, [partBomItem?.id]);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const notesValue = watch('notes');
  const partValue = watch('part');

  const isBomType = useMemo(() => {
    if (!partValue) {
      return false;
    }

    return PartType.BOM === partValue.type;
  }, [partValue]);

  const { onAddNote, onDeleteNote, onEditNote } = useNotes({
    notes: notesValue,
    setNotes: (value: IManagedNote[]) => setValue('notes', value),
    isEdit,
  });

  const onValidatePart = async (
    part: Partial<IPart> & IAutocompleteOption,
    manufacturer?: IManufacturer
  ) => {
    setIsValidating(true);

    const {
      success,
      record: validatedPart,
      extraData: uoms,
    } = await validatePart(
      {
        number: part.number,
        manufacturerId: manufacturer?.id || part.manufacturerId || null,
      },
      {
        onSuccess: () => setIsValidating(false),
        onError: () => setIsValidating(false),
      }
    );

    if (!success) {
      setValue('part', null);
      setValue('manufacturer', null);
      setValue('description', null);

      setUomOptions([]);
      setVerifiedPart(false);

      return;
    }

    const previousPart = getValues('part');
    const previousManufacturer = getValues('manufacturer');
    const previousDescription = getValues('description');

    setValue('part', {
      ...previousPart,
      ...validatedPart,
      number: PartNumberHelper.toLabel(validatedPart?.number || previousPart?.number),
    });

    setValue(
      'manufacturer',
      validatedPart?.manufacturerId
        ? {
            id: validatedPart.manufacturerId,
            name: validatedPart.manufacturerName,
          }
        : previousManufacturer
    );
    setValue(
      'description',
      validatedPart?.description || validatedPart?.descriptionLong || previousDescription
    );
    setValue('uom', uoms?.[0]);

    setUomOptions(uoms?.map((uom: string) => ({ id: uom, name: uom })));
    setVerifiedPart(Boolean(validatedPart?.verified));

    setFocusElementName(
      getFocusElementNameAfterValidate(
        Boolean(validatedPart?.verified),
        !!getValues('manufacturer'),
        validatedPart?.type as PartType
      )
    );
  };

  const onChangePartNumber = (part: Partial<IPart> & IAutocompleteOption) => {
    if (!part) {
      reset();

      setUomOptions([]);
      setVerifiedPart(false);

      return;
    }

    onValidatePart(part, getValues('manufacturer'));
  };

  const onKeyDownPartNumber = (event: KeyboardEvent<HTMLInputElement>, value: string) => {
    if (isEnterKeyPress(event) || isTabKeyPress(event)) {
      event.preventDefault();

      const part = { number: value };

      setValue('part', part);

      onChangePartNumber(part);
    }
  };

  const onChangeManufacturer = (manufacturer: IManufacturer) => {
    const partValue = getValues('part');

    if (!manufacturer && !partValue) {
      return;
    }

    onValidatePart(partValue, manufacturer);
  };

  const handleOnClose = (reset: UseFormReset<IUpsertItemFormData>) => {
    setIsValidating(false);
    setVerifiedPart(false);
    setUomOptions([]);

    onClose(reset);
  };

  return (
    <Form
      isOpen={isOpen}
      onClose={handleOnClose}
      title={isEdit ? t('titleEditPartBomItem') : t('titleAddPartBomItem')}
      submitBtnLabel={t('buttonLabelSave')}
      onSubmit={onSubmit}
      level={0}
    >
      <AdvancedPartNumberFormField
        onChangePartNumber={onChangePartNumber}
        onKeyDownPartNumber={onKeyDownPartNumber}
        partAutocompletePayload={{
          filters: {
            type: 'bom',
          },
        }}
        disabled={isValidating || isEdit}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        level={1}
      />

      {!isBomType && (
        <FormField
          name="manufacturer"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelManufacturer'),
            optionKey: 'name',
            placeholder: t('placeholderChooseManufacturer'),
            getOptions: manufacturerService.getMany,
            requestPayload: {
              alternateVersionEnabled: isAlternateVersionEnabled ? 1 : 0,
            },
            getLabel: (option: IManufacturer) => option.name,
            onBlur: (_: SyntheticEvent, option: IManufacturer) => onChangeManufacturer(option),
            onChange: (_: SyntheticEvent, option: IManufacturer) => onChangeManufacturer(option),
            hasCreateOption: true,
            disabled: verifiedPart || isValidating,
          }}
        />
      )}

      <FormField
        name="description"
        type={InputType.TEXTAREA}
        inputProps={{
          label: t('labelDescription'),
          placeholder: t('placeholderUpsertLineDescription'),
          onKeyDown: preventEnter,
          maxRows: 6,
          disabled: verifiedPart || isBomType || isValidating,
          required: true,
        }}
      />

      <FormGroupWrapper name="quantityUom">
        <FormField
          name="quantity"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t('labelQuantity'),
            placeholder: t('placeholderQuantity'),
            slotProps: {
              input: {
                min: 0,
              },
            },
            disabled: isValidating || !partValue,
            required: true,
          }}
        />

        <FormField
          name="uom"
          type={InputType.SELECT}
          inputProps={{
            label: t('labelUom'),
            placeholder: t('placeholderUpsertLineUom'),
            getLabel: (option: IUom) => option?.name,
            getValue: (option: IUom) => option?.name,
            options: uomOptions,
            disabled: isValidating || !partValue,
            required: true,
          }}
        />
      </FormGroupWrapper>

      {!!partValue && (
        <Stack gap={0.5}>
          {notesValue?.length > 0 && (
            <FormLabel>{`${t('labelNotes')} (${notesValue.length})`}</FormLabel>
          )}

          <Notes
            notes={notesValue}
            noteProps={{
              onDeleteNote,
              onSubmitEditNote: onEditNote,
            }}
          />

          <AddNoteButton
            partBomItem={partBomItem}
            onSubmit={!isEdit ? onAddNote : null}
            level={1}
            buttonProps={{
              startDecorator: <Plus />,
              sx: { width: 'fit-content' },
            }}
          />
        </Stack>
      )}
    </Form>
  );
};
